import { newId } from '@quickreport/domain';
import type { LocalDb, Tx } from './types';

/** Device-level settings live under this organization id in sync_state. */
export const NIL_ORG = '00000000-0000-0000-0000-000000000000';

type Q = Pick<LocalDb, 'query'> | Tx;

export async function kvGet(db: Q, key: string, orgId = NIL_ORG): Promise<string | null> {
  const r = await db.query<{ value: string }>(
    'select value from sync_state where org_id = $1 and key = $2',
    [orgId, key],
  );
  return r.rows[0]?.value ?? null;
}

export async function kvSet(db: Q, key: string, value: string, orgId = NIL_ORG): Promise<void> {
  await db.query(
    `insert into sync_state (org_id, key, value) values ($1, $2, $3)
     on conflict (org_id, key) do update set value = excluded.value`,
    [orgId, key, value],
  );
}

export async function kvDelete(db: Q, key: string, orgId = NIL_ORG): Promise<void> {
  await db.query('delete from sync_state where org_id = $1 and key = $2', [orgId, key]);
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** Called whenever the app changes local data that must reach the server. */
export function onLocalChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function emitLocalChange(): void {
  for (const fn of listeners) fn();
}

/**
 * Record that a row changed and must be sent to the server. Call inside the same
 * transaction that changed the row, so the change and its queue entry succeed together.
 */
export async function queueChange(tx: Q, table: string, rowId: string): Promise<void> {
  await tx.query(
    `insert into outbox (id, table_name, row_id, op, base_rev) values ($1, $2, $3, 'upsert', 0)`,
    [newId(), table, rowId],
  );
  // Let the engine know once the transaction is done (next microtask is good enough here,
  // the engine re-reads the outbox from the database).
  queueMicrotask(emitLocalChange);
}
